import { useMemo } from "react";
import { FileDown, Pencil, PlusCircle } from "lucide-react";
import { useLocalization } from "@/context/localization-context";
import type { TranslationKey } from "@/i18n/messages";
import { shortDateTime } from "@/lib/date-format";
import { Ago, EmptyBlock, ErrorBlock, LoadingBlock } from "@/monitoring/components/primitives";
import { errorMessage, useCatalogHistory } from "../api";
import { componentDiff, parseStamp } from "../model";
import type { CatalogIngredient, CatalogResponse, HistoryItem, ImportChanges, RecipeComponent } from "../types";
import { IngredientChip } from "./shared";

/** Imports of the customer's package and edits in the admin, newest first. */
export function HistoryTab({ catalog }: { catalog: CatalogResponse }) {
  const { t } = useLocalization();
  const history = useCatalogHistory(100);
  const byId = useMemo(() => new Map(catalog.ingredients.map((i) => [i.id, i])), [catalog.ingredients]);

  return (
    <div className="flex flex-col gap-3">
      <p className="max-w-3xl text-xs text-muted-foreground">{t("catalog.history.note")}</p>
      {history.error && <ErrorBlock message={errorMessage(history.error)} onRetry={() => void history.refetch()} />}
      {history.isLoading && <LoadingBlock height={200} />}
      {history.data && history.data.items.length === 0 && <EmptyBlock>{t("catalog.history.empty")}</EmptyBlock>}
      {history.data && history.data.items.length > 0 && (
        <ol className="divide-y rounded-md border">
          {history.data.items.map((item, index) => (
            <li key={`${item.at}-${index}`} className="flex min-w-0 gap-3 px-3 py-2.5">
              <ItemIcon item={item} />
              <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm">
                  <span className="min-w-0 break-words">{item.type === "import" ? <ImportTitle item={item} /> : <EditTitle item={item} />}</span>
                  <span className="ml-auto flex shrink-0 items-center gap-1 text-xs text-muted-foreground" title={shortDateTime(item.at)}>
                    <Ago ts={parseStamp(item.at)} />
                  </span>
                </div>
                {item.type === "import" ? <ImportDetail changes={item.changes} /> : <EditDetail item={item} byId={byId} />}
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function ItemIcon({ item }: { item: HistoryItem }) {
  const Icon = item.type === "import" ? FileDown : item.action === "created" ? PlusCircle : Pencil;
  return <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />;
}

function ImportTitle({ item }: { item: Extract<HistoryItem, { type: "import" }> }) {
  const { t } = useLocalization();
  return <>{t("catalog.history.import", { who: item.by, name: item.name })}</>;
}

function EditTitle({ item }: { item: Extract<HistoryItem, { type: "edit" }> }) {
  const { t } = useLocalization();
  const after = item.after as { name?: string; version?: number };
  return (
    <>
      {t(`catalog.history.${item.action}.${item.kind}`, { who: item.by, name: after.name ?? item.id, id: item.id })}
      {item.kind === "recipe" && after.version !== undefined && (
        <span className="text-muted-foreground"> · {t("catalog.recipe.version", { version: after.version })}</span>
      )}
    </>
  );
}

function ImportDetail({ changes }: { changes: { ingredients: ImportChanges; recipes: ImportChanges; products: ImportChanges } }) {
  const { t } = useLocalization();
  const parts = (["recipes", "ingredients", "products"] as const)
    .map((kind) => {
      const c = changes[kind];
      if (!c.created.length && !c.updated.length) return null;
      return t(`catalog.history.importPart.${kind}`, { created: c.created.length, updated: c.updated.length });
    })
    .filter(Boolean);
  const overwritten = (["recipes", "ingredients", "products"] as const).flatMap((kind) => changes[kind].edited ?? []);
  return (
    <div className="flex flex-col gap-0.5 text-xs text-muted-foreground">
      <span>{parts.length ? parts.join(" · ") : t("catalog.history.importNothing")}</span>
      {overwritten.length > 0 && (
        <span className="text-amber-300">{t("catalog.history.overwritten", { ids: overwritten.join(", ") })}</span>
      )}
    </div>
  );
}

const asStrings = (value: unknown): string[] => (Array.isArray(value) ? value.map(String) : []);

/** labels of the fields of an ingredient or a product edit */
const FIELD_LABELS: Record<string, TranslationKey> = {
  name: "catalog.history.field.name",
  aliases: "catalog.history.field.aliases",
  detector_classes: "catalog.history.field.detector_classes",
  kind: "catalog.history.field.kind",
  recipe_id: "catalog.history.field.recipe_id",
};

function EditDetail({ item, byId }: { item: Extract<HistoryItem, { type: "edit" }>; byId: Map<string, CatalogIngredient> }) {
  const { t } = useLocalization();
  const before = item.before ?? {};
  const after = item.after;

  if (item.kind === "recipe") {
    const old = (before.components as RecipeComponent[] | undefined) ?? [];
    const next = (after.components as RecipeComponent[] | undefined) ?? [];
    const diff = item.before ? componentDiff(old, next) : { added: next.map((c) => c.ingredient_id), removed: [], moved: [] };
    return (
      <div className="flex flex-wrap items-center gap-1 text-xs">
        {diff.added.map((id) => (
          <span key={`+${id}`} className="inline-flex items-center gap-1 text-emerald-300">
            +<IngredientChip ingredient={byId.get(id)} fallbackId={id} />
          </span>
        ))}
        {diff.removed.map((id) => (
          <span key={`-${id}`} className="inline-flex items-center gap-1 text-red-300">
            −<IngredientChip ingredient={byId.get(id)} fallbackId={id} className="line-through" />
          </span>
        ))}
        {diff.moved.map((id) => (
          <span key={`~${id}`} className="inline-flex items-center gap-1 text-amber-300">
            ~<IngredientChip ingredient={byId.get(id)} fallbackId={id} afterBaking={!!next.find((c) => c.ingredient_id === id)?.after_baking} />
          </span>
        ))}
        {item.before && before.name !== after.name && (
          <span className="text-muted-foreground">{t("catalog.recipe.renamed", { from: String(before.name), to: String(after.name) })}</span>
        )}
      </div>
    );
  }

  const lines: string[] = [];
  for (const [key, labelKey] of Object.entries(FIELD_LABELS)) {
    if (!(key in after)) continue;
    const label = t(labelKey);
    const value = after[key];
    const old = (before as Record<string, unknown>)[key];
    if (Array.isArray(value)) {
      const added = asStrings(value).filter((v) => !asStrings(old).includes(v));
      const removed = asStrings(old).filter((v) => !asStrings(value).includes(v));
      if (added.length || removed.length) {
        lines.push(`${label}: ${[...added.map((v) => `+ ${v}`), ...removed.map((v) => `− ${v}`)].join(", ")}`);
      }
    } else if (old !== value) {
      lines.push(item.before ? `${label}: ${String(old ?? "–")} → ${String(value ?? "–")}` : `${label}: ${String(value ?? "–")}`);
    }
  }
  if (!lines.length) return null;
  return (
    <ul className="flex flex-col gap-0.5 text-xs text-muted-foreground">
      {lines.map((line) => (
        <li key={line} className="break-words">
          {line}
        </li>
      ))}
    </ul>
  );
}
